import type {
  ChatTransportConfig,
  ChatRequestInput,
  Context,
} from '@cowbell/moo';
import { retrieveAuthTokens } from '../../../utils/next/auth.utils';
import {
  getScreenData,
  mergeScreenDataIntoContexts,
} from '../context/screen-data-supplier';
import { mergeViewSurfaceIntoContexts } from '@cowbell/surface-broker/chat';
import type { UserRole } from '../types';

/** Translate the library's `kind:` discriminator to V1's `type:` wire shape.
 *  V1's chat-agent backend expects `type` — sending `kind` makes it silently
 *  drop the context, so the agent never sees what view the user is on. */
function toWireContext(c: Context): Record<string, unknown> {
  const { kind, ...rest } = c as Context & { kind: string };
  return { type: kind, ...rest };
}

/** Both paths the account questionnaire tab is mounted at. */
const RISK_DATA_PATHS = /\/(?:risk-questions|risk-data)\/?$/;

/** The Risk Data tab's "nothing loaded" sentinel — the page renders no form. */
const EMPTY_FORM_ID = 'default';

const UUID_RE =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * The questionnaire the user has open, as a `<form id="…"/>` tag.
 *
 * The tab keeps its loaded form in `?formId=` and the screen chip carries only
 * the pathname, so without this the agent has to ask the user to recite a UUID.
 * Emitted on every send (unlike the entity tags, which are change-only): the
 * user can load a different form mid-conversation, and we want the newest
 * message to name the form currently open. Shape-checked because a hand-edited
 * URL reaches the agent as an id it may pass to a tool.
 */
function activeFormTag(): string {
  if (typeof window === 'undefined') return '';
  const { pathname, search } = window.location;
  if (!RISK_DATA_PATHS.test(pathname)) return '';
  const formId = new URLSearchParams(search).get('formId')?.trim() ?? '';
  if (!formId || formId === EMPTY_FORM_ID || !UUID_RE.test(formId)) return '';
  return `<form id="${formId}"/>`;
}

interface EntityRef {
  readonly type: string;
  readonly id: string;
}

/** Collect the entities referenced by the attached chips, in wire order,
 *  deduped by id. Any chip that carries both an `entityType` and `entityId`
 *  counts — that spans user-attached `entity` chips AND the auto-derived
 *  `screen` chip for an entity-detail page (the one visible in the strip). */
function collectEntityRefs(contexts: Record<string, unknown>[]): EntityRef[] {
  const seen = new Set<string>();
  const refs: EntityRef[] = [];
  for (const c of contexts) {
    const id = c.entityId;
    const type = c.entityType;
    if (typeof id !== 'string' || typeof type !== 'string') continue;
    if (seen.has(id)) continue;
    seen.add(id);
    refs.push({ type, id });
  }
  return refs;
}

/** Order-independent equality for two id lists. Uses `Set.has` for lookup but
 *  never iterates a Set — some test environments (jsdom) ship a Set whose
 *  spread/`for…of` yields nothing even though `.has`/`.size` work. */
function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const bIds = new Set(b);
  return a.every((id) => bIds.has(id));
}

/**
 * Factory that creates a ChatTransportConfig for the Cowbell chat stream
 * endpoint. The body shape mirrors V1's prepareSendMessagesRequest so the
 * backend receives an identical payload regardless of which shell is active.
 *
 * `role` is dynamic (derives from the current user's JWT accountType) so it
 * is captured at factory time via the argument rather than re-computed on
 * every send.
 */
export function makeCowbellChatTransport(role: UserRole): ChatTransportConfig {
  // The entity-id set the backend has confirmed receiving, per conversation.
  // We only re-inject the `<entity/>` tags when the attached set changes
  // (add / remove / swap) — the backend keeps the ids in conversation history,
  // so repeating them every turn just bloats the transcript. Keyed by
  // conversationId; a fresh conversation (new id) starts with no prior set, so
  // its first change re-tags. The pre-conversation send (null id) always tags
  // and is never staged, which sidesteps the 'pending' key colliding across
  // New Chats — at the cost of one accepted duplicate on the following turn
  // once the real id arrives.
  const committedEntityIds = new Map<string, string[]>();
  // Ids sent in the latest not-yet-confirmed send, per conversation. Staged by
  // `buildRequestBody` and promoted to `committedEntityIds` only when
  // `onSendSucceeded` fires — so a failed / cancelled send leaves the set
  // uncommitted and the retry re-sends the tags.
  const pendingEntityIds = new Map<string, string[]>();

  return {
    endpoint: '/api/chat-agent/v1/chat/stream',

    getHeaders(): Record<string, string> {
      const { accessToken } = retrieveAuthTokens();
      const headers: Record<string, string> = {
        'X-Request-Id': crypto.randomUUID(),
        // Lets chat-agent tag Langfuse traces with the originating client
        // (mobile apps send ios/android).
        'X-Client-Platform': 'web',
      };
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
      }
      return headers;
    },

    buildRequestBody({
      messageId,
      agentId,
      conversationId,
      text,
      contexts,
      skill,
      tone,
      webSearch,
    }: ChatRequestInput): Record<string, unknown> {
      // Capture-at-send page data (e.g. the P100 edit-quote page's current
      // pricing payload), merged into its own screen chip only — V1 parity
      // with useAskMooChat's transport. Merged AFTER toWireContext so the
      // chips carry `type: 'screen'` here just like V1's.
      //
      // The surface broker layers on top under `entityData.onScreenView`.
      // Both merges are keyed on entityId and write disjoint keys, so a page
      // can use either or both; a dismissed chip still means nothing is sent,
      // which is the consent semantic we want to keep. The broker's chat entry
      // point owns the wire key and reports its own delivery.
      const merged = mergeViewSurfaceIntoContexts(
        mergeScreenDataIntoContexts(
          contexts.map(toWireContext),
          getScreenData()
        )
      );

      // Prepend attached entities as `<entity type id/>` tags so the model
      // gets the ids inline in the message, but only when the set changed
      // since the last *confirmed* send in this conversation (see the maps'
      // comments). Wire-only: `text` — not `message` — feeds the composer's
      // optimistic bubble, so the user still sees their clean prompt.
      const entityRefs = collectEntityRefs(merged);
      const currentIds = entityRefs.map((r) => r.id);
      const previousIds = conversationId
        ? committedEntityIds.get(conversationId) ?? []
        : [];
      const entitySetChanged = !sameIds(currentIds, previousIds);
      // Stage, don't commit: only `onSendSucceeded` promotes this to the
      // committed set, so a failed send doesn't suppress the retry's tags.
      if (conversationId) {
        pendingEntityIds.set(conversationId, currentIds);
      }
      const entityTags =
        entitySetChanged && entityRefs.length > 0
          ? entityRefs
              .map((r) => `<entity type="${r.type}" id="${r.id}"/>`)
              .join('\n')
          : '';
      // Wire-only, like the entity tags: `text` feeds the optimistic bubble, so
      // the user still sees their clean prompt without the tags.
      const tags = [entityTags, activeFormTag()].filter(Boolean).join('\n');
      const message = tags ? `${tags}\n\n${text}` : text;

      return {
        messageId,
        agentId,
        role,
        message,
        contexts: merged,
        conversationId,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        // V1 parity: forward optional `skill` so quick-reply / email-draft
        // routing reaches the backend's skill router. Omit the key
        // entirely when the caller didn't set one — V1's wire shape
        // distinguishes missing from null.
        ...(skill ? { skill } : {}),
        // Backend expects `toneId` (not `tone`). Omit entirely when unset
        // so the backend sees no-tone, not an explicit null.
        ...(tone ? { toneId: tone } : {}),
        // Composer web-search switch; only the off state is sent (OMNI-820).
        ...(webSearch === false ? { webSearch: false } : {}),
      };
    },

    // Promote the staged id set to committed once the backend confirms the
    // send. Called from the library's `onFinish`, so a failed / cancelled send
    // (which never reaches here) leaves the set uncommitted — the retry then
    // sees an unchanged-but-uncommitted set and re-sends the tags.
    onSendSucceeded(conversationId: string | null): void {
      if (!conversationId) return;
      const pending = pendingEntityIds.get(conversationId);
      if (!pending) return;
      committedEntityIds.set(conversationId, pending);
      pendingEntityIds.delete(conversationId);
    },
  };
}
